import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plug, Check, Loader2, Trash2, KeyRound, ExternalLink, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import LeftRail from "@/components/LeftRail";
import { getConnections, setConnection, removeConnection, getBalances } from "@/lib/falApi";

const PROVIDERS = [
  { id: "fal", name: "fal.ai", desc: "Image & video generation across the latest models.", url: "https://fal.ai/dashboard/keys", ph: "id:secret" },
  { id: "kie", name: "Kie.ai", desc: "FLUX.2 & more via your Kie credits — appears in Create & Compare.", url: "https://kie.ai/api-key", ph: "your Kie API key" },
  { id: "wavespeed", name: "WaveSpeed.ai", desc: "Fast, low-cost image & video (Z-Image, FLUX, Seedance). Needs a small top-up to activate.", url: "https://wavespeed.ai/accesskey", ph: "wsk_live_..." },
  { id: "elevenlabs", name: "ElevenLabs", desc: "Text-to-speech, voice cloning & sound effects.", url: "https://elevenlabs.io/app/settings/api-keys", ph: "sk_..." },
];
const SOON = ["OpenAI / Claude / Gemini (LLM)", "Music generation"];

export default function Connections() {
  const [status, setStatus] = useState(null);
  const [balances, setBalances] = useState({});
  const [inputs, setInputs] = useState({});
  const [busy, setBusy] = useState("");

  const load = async () => {
    try {
      const st = await getConnections();
      setStatus(st);
      if (st.wavespeed || st.kie) {
        getBalances().then(setBalances).catch((e) => console.error("Failed to load balances", e));
      }
    } catch (e) { console.error("Failed to load connections", e); }
  };
  useEffect(() => { document.documentElement.classList.add("dark"); load(); }, []);

  const save = async (id) => {
    const key = (inputs[id] || "").trim();
    if (!key) { toast.error("Paste a key first"); return; }
    setBusy(id);
    try {
      const res = await setConnection(id, key);
      if (res.valid) {
        toast.success(`${id} connected & verified`);
        setInputs((s) => ({ ...s, [id]: "" }));
        load();
      } else {
        const map = { unauthorized: "Key rejected — invalid or expired.", format: "That doesn't look like a valid key.", empty: "Key is empty." };
        toast.error(map[res.reason] || "Could not verify this key.");
      }
    } catch (e) { toast.error("Could not save key"); }
    finally { setBusy(""); }
  };

  const remove = async (id) => {
    await removeConnection(id).catch(() => {});
    toast.success("Disconnected");
    load();
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white flex" data-testid="connections-page">
      <LeftRail />
      <div className="flex-1 min-w-0 overflow-y-auto">
        <header className="h-14 border-b border-zinc-800 flex items-center px-6 gap-3">
          <Plug className="h-5 w-5 text-[#faff69]" />
          <h1 className="font-display font-bold text-lg tracking-tight">Connections</h1>
        </header>
        <div className="max-w-2xl mx-auto p-6 space-y-4">
          <p className="text-sm text-zinc-400">Add your own API key for each service you use. Keys are stored on your account, used server-side, and sync across your devices.</p>

          {PROVIDERS.map((p) => (
            <div key={p.id} className="rounded-xl border border-zinc-800 bg-[#121212] p-5" data-testid={`connection-${p.id}`}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-display font-semibold text-base">{p.name}</span>
                    {status === null
                      ? <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]" data-testid={`status-${p.id}`}>Checking…</Badge>
                      : status[p.id]
                        ? <Badge className="bg-[#34C759]/15 text-[#34C759] border-0 text-[10px] gap-1" data-testid={`status-${p.id}`}><Check className="h-3 w-3" /> Connected</Badge>
                        : <Badge variant="outline" className="border-zinc-700 text-zinc-500 text-[10px]" data-testid={`status-${p.id}`}>Not connected</Badge>}
                    {status?.[p.id] && balances[p.id] && balances[p.id].balance != null && (
                      <Badge
                        data-testid={`balance-${p.id}`}
                        className={`border-0 text-[10px] gap-1 ${p.id === "wavespeed" && Number(balances[p.id].balance) < 1 ? "bg-amber-500/15 text-amber-400" : "bg-[#faff69]/10 text-[#faff69]"}`}>
                        <Wallet className="h-3 w-3" />
                        {p.id === "wavespeed" ? `$${Number(balances[p.id].balance).toFixed(2)}` : `${balances[p.id].balance} credits`}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">{p.desc}</p>
                </div>
                <a href={p.url} target="_blank" rel="noreferrer" className="text-[11px] text-[#faff69] hover:underline flex items-center gap-1 shrink-0">
                  Get key <ExternalLink className="h-3 w-3" />
                </a>
              </div>
              <div className="flex gap-2 mt-4">
                <Input data-testid={`input-${p.id}`} type="password" value={inputs[p.id] || ""} placeholder={status?.[p.id] ? "•••••••• (replace)" : p.ph}
                  onChange={(e) => setInputs((s) => ({ ...s, [p.id]: e.target.value }))}
                  className="bg-[#0a0a0a] border-zinc-800 focus-visible:ring-[#faff69] font-mono" />
                <Button onClick={() => save(p.id)} disabled={busy === p.id} data-testid={`save-${p.id}`} className="bg-[#faff69] hover:bg-[#e6eb52] text-black shrink-0">
                  {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><KeyRound className="h-4 w-4 mr-1.5" /> Save</>}
                </Button>
                {status?.[p.id] && (
                  <Button variant="ghost" onClick={() => remove(p.id)} data-testid={`remove-${p.id}`} className="text-zinc-400 hover:text-[#FF3B30] shrink-0">
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}

          <div className="rounded-xl border border-dashed border-zinc-800 p-5">
            <div className="text-xs font-semibold tracking-[0.2em] uppercase text-zinc-500 mb-2">Coming soon</div>
            <div className="flex flex-wrap gap-2">
              {SOON.map((s) => <span key={s} className="text-[11px] font-mono px-2.5 py-1 rounded-full bg-zinc-900 text-zinc-500 border border-zinc-800">{s}</span>)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
